import { useState, useEffect } from 'react';
import i18n from 'i18next';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  X,
  Edit2,
  Save,
  Trash2,
  Calendar,
  Hash,
  Clock,
  Copy,
  Check,
  AlertCircle,
} from 'lucide-react';
import { apiService, Memory } from '../services/api';
import {
  getMemoryAudioTranscription,
  getPlayableMemoryAudioUrl,
  memoryHasAudio,
} from '../lib/memoryMedia';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog } from './ui/ConfirmationDialog';
import { cn } from '@/lib/utils';

const workspaceToolbarOutlineClass =
  'border-workspace-border bg-workspace-background-secondary text-workspace-text-primary shadow-sm hover:bg-workspace-background-tertiary focus-visible:ring-2 focus-visible:ring-workspace-primary-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-workspace-background-primary';

const workspaceToolbarDestructiveClass =
  'border-red-500/40 bg-workspace-background-secondary text-red-400 shadow-sm hover:bg-red-950/50 hover:text-red-300 focus-visible:ring-2 focus-visible:ring-red-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-workspace-background-primary';

interface MemoryDetailModalProps {
  memoryId: string;
  isOpen: boolean;
  onClose: () => void;
  onMemoryUpdate: (memory: Memory) => void;
  onMemoryDelete: (memoryId: string) => void;
}

export interface MemoryDetailLoadedViewProps {
  memory: Memory;
  mode: 'view' | 'edit';
  editContent: string;
  editTags: string;
  onEditContentChange: (value: string) => void;
  onEditTagsChange: (value: string) => void;
  hasChanges: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  copied: boolean;
  actionError: string;
  onCopy: () => void;
  onSave: () => void;
  onRequestDelete: () => void;
  onCancelEdit: () => void;
  onEnterEdit: () => void;
}

export function MemoryDetailLoadedView({
  memory,
  mode,
  editContent,
  editTags,
  onEditContentChange,
  onEditTagsChange,
  hasChanges,
  isUpdating,
  isDeleting,
  copied,
  actionError,
  onCopy,
  onSave,
  onRequestDelete,
  onCancelEdit,
  onEnterEdit,
}: MemoryDetailLoadedViewProps) {
  const { t, i18n } = useTranslation('memories');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        <div className="min-w-0 pr-10 sm:pr-0">
          <h2 className="text-xl font-semibold text-workspace-text-primary mb-2">
            {mode === 'edit'
              ? t('memoryDetail.titles.edit')
              : t('memoryDetail.titles.view')}
          </h2>
          <div className="flex flex-col gap-1 text-sm text-workspace-text-secondary sm:flex-row sm:items-center sm:gap-4">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4 shrink-0" aria-hidden />
              <span>
                {t('memoryDetail.created', {
                  date: formatDate(memory.created_at),
                })}
              </span>
            </div>
            {memory.updated_at && memory.updated_at !== memory.created_at && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 shrink-0" aria-hidden />
                <span>
                  {t('memoryDetail.updated', {
                    date: formatDate(memory.updated_at),
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {mode === 'view' ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onCopy}
                className={workspaceToolbarOutlineClass}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    {t('memoryDetail.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    {t('memoryDetail.copy')}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onEnterEdit}
                className={workspaceToolbarOutlineClass}
              >
                <Edit2 className="w-4 h-4" />
                {t('memoryDetail.buttons.edit')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onRequestDelete}
                disabled={isDeleting}
                className={workspaceToolbarDestructiveClass}
                aria-label={t('actions.delete')}
              >
                {isDeleting ? (
                  <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onCancelEdit}
                className={workspaceToolbarOutlineClass}
              >
                {t('memoryDetail.buttons.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={onSave}
                disabled={!hasChanges || isUpdating}
                className="bg-workspace-primary-500 hover:bg-workspace-primary-600 text-white focus-visible:ring-2 focus-visible:ring-workspace-primary-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-workspace-background-primary"
              >
                {isUpdating ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('memoryDetail.saving')}</span>
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('memoryDetail.buttons.saveChanges')}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {actionError ? (
        <div
          className="rounded-md border border-red-500/30 bg-red-950/30 p-4 flex items-start gap-2"
          role="alert"
        >
          <AlertCircle className="w-5 h-5 shrink-0 text-red-400" aria-hidden />
          <span className="text-red-200">{actionError}</span>
        </div>
      ) : null}

      <Card className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg">
        <CardHeader>
          <CardTitle className="text-workspace-text-primary">
            {t('memoryDetail.sections.content')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mode === 'view' ? (
            <div className="prose prose-sm max-w-none">
              <p className="font-sans text-workspace-text-primary leading-relaxed whitespace-pre-wrap">
                {memory.content}
              </p>
            </div>
          ) : (
            <Textarea
              value={editContent}
              onChange={(e) => onEditContentChange(e.target.value)}
              placeholder={t('memoryDetail.contentPlaceholder')}
              className="min-h-[200px] bg-workspace-input border-workspace-border text-workspace-text-primary placeholder:text-workspace-text-tertiary focus:border-workspace-primary-400 focus:ring-workspace-primary-400/20 resize-none"
            />
          )}
        </CardContent>
      </Card>

      {memoryHasAudio(memory) && mode === 'view' && (
        <Card className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg">
          <CardHeader>
            <CardTitle className="text-workspace-text-primary">
              {t('memoryDetail.audio.sectionTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(() => {
              const playUrl = getPlayableMemoryAudioUrl(memory);
              return playUrl ? (
                <div>
                  <p className="text-xs text-workspace-text-tertiary mb-2">
                    {t('memoryDetail.audio.playerLabel')}
                  </p>
                  <audio
                    controls
                    className="w-full max-h-10"
                    src={playUrl}
                    preload="metadata"
                    aria-label={t('memoryDetail.audio.playerLabel')}
                  />
                </div>
              ) : null;
            })()}
            <div>
              <p className="text-xs text-workspace-text-tertiary mb-1">
                {t('memoryDetail.audio.transcription')}
              </p>
              <p className="text-sm text-workspace-text-primary whitespace-pre-wrap">
                {getMemoryAudioTranscription(memory) ??
                  t('memoryDetail.audio.noTranscription')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg">
        <CardHeader>
          <CardTitle className="text-workspace-text-primary flex items-center gap-2">
            <Hash className="w-5 h-5 shrink-0" aria-hidden />
            <span>{t('memoryDetail.sections.tags')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mode === 'view' ? (
            <div className="space-y-2">
              {memory.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {memory.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="bg-workspace-primary-100 text-workspace-primary-700 border-workspace-primary-200"
                    >
                      #{tag}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-workspace-text-tertiary">
                  {t('memoryDetail.noTags')}
                </p>
              )}
            </div>
          ) : (
            <Input
              value={editTags}
              onChange={(e) => onEditTagsChange(e.target.value)}
              placeholder={t('memoryDetail.tagsPlaceholder')}
              className="bg-workspace-input border-workspace-border text-workspace-text-primary placeholder:text-workspace-text-tertiary focus:border-workspace-primary-400 focus:ring-workspace-primary-400/20"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface MemoryDetailEditProps {
  memoryId: string;
  mode: 'view' | 'edit';
  onModeChange: (mode: 'view' | 'edit') => void;
  onUpdate: (memory: Memory) => void;
  onDelete: () => void;
  onClose: () => void;
}

const MemoryDetailEdit: React.FC<MemoryDetailEditProps> = ({
  memoryId,
  mode,
  onModeChange,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const analytics = useAnalytics();
  const { t } = useTranslation('memories');
  const [memory, setMemory] = useState<Memory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const [editContent, setEditContent] = useState('');
  const [editTags, setEditTags] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const loadMemory = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        setActionError('');
        const memoryData = await apiService.getMemoryById(memoryId);
        setMemory(memoryData);
        setEditContent(memoryData.content);
        setEditTags(memoryData.tags.join(', '));
      } catch (err: unknown) {
        const fallback = i18n.t('memoryDetail.errors.loadFailed', { ns: 'memories' });
        const message = err instanceof Error ? err.message : '';
        setLoadError(message || fallback);
        console.error('Failed to load memory:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (memoryId) {
      void loadMemory();
    }
  }, [memoryId]);

  useEffect(() => {
    if (memory) {
      const contentChanged = editContent !== memory.content;
      const tagsChanged = editTags !== memory.tags.join(', ');
      setHasChanges(contentChanged || tagsChanged);
    }
  }, [editContent, editTags, memory]);

  const handleSave = async () => {
    if (!memory || !hasChanges) return;

    try {
      setIsUpdating(true);
      setActionError('');

      const tags = editTags
        .split(/,/)
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const updatedMemory = await apiService.updateMemory(memory.id, {
        content: editContent.trim(),
        tags,
      });

      setMemory(updatedMemory);
      setHasChanges(false);
      onUpdate(updatedMemory);
      onModeChange('view');

      analytics.trackEvent('memory_updated', {
        memoryId: memory.id,
        contentLength: editContent.length,
        tagCount: tags.length,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('memoryDetail.errors.updateFailed');
      setActionError(message || t('memoryDetail.errors.updateFailed'));
      console.error('Failed to update memory:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const performDelete = async () => {
    if (!memory) return;

    try {
      setIsDeleting(true);
      setActionError('');
      await apiService.deleteMemory(memory.id, memory.space_id);
      onDelete();
      onClose();

      analytics.trackEvent('memory_deleted', {
        memoryId: memory.id,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('memoryDetail.errors.deleteFailed');
      setActionError(message || t('memoryDetail.errors.deleteFailed'));
      console.error('Failed to delete memory:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopy = async () => {
    if (!memory) return;

    try {
      await navigator.clipboard.writeText(memory.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      analytics.trackEvent('memory_copied', {
        memoryId: memory.id,
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const resetEditToMemory = () => {
    setEditContent(memory?.content || '');
    setEditTags(memory?.tags.join(', ') || '');
    setHasChanges(false);
    onModeChange('view');
  };

  const handleCancelEdit = () => {
    if (hasChanges) {
      setDiscardDialogOpen(true);
      return;
    }
    resetEditToMemory();
  };

  const confirmDiscardEdit = () => {
    resetEditToMemory();
    setDiscardDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-4 border-workspace-primary-200 border-t-workspace-primary-600 rounded-full animate-spin" />
          <span className="text-workspace-text-primary">
            {t('memoryDetail.loading')}
          </span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex items-center gap-3 text-red-400">
          <AlertCircle className="w-6 h-6 shrink-0" aria-hidden />
          <span>{loadError}</span>
        </div>
      </div>
    );
  }

  if (!memory) return null;

  return (
    <>
      <MemoryDetailLoadedView
        memory={memory}
        mode={mode}
        editContent={editContent}
        editTags={editTags}
        onEditContentChange={setEditContent}
        onEditTagsChange={setEditTags}
        hasChanges={hasChanges}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        copied={copied}
        actionError={actionError}
        onCopy={handleCopy}
        onSave={handleSave}
        onRequestDelete={() => setDeleteDialogOpen(true)}
        onCancelEdit={handleCancelEdit}
        onEnterEdit={() => onModeChange('edit')}
      />

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          void performDelete();
        }}
        title={t('confirmations.delete')}
        description={t('confirmations.deleteDescription')}
        confirmText={t('confirmations.deleteConfirm')}
        cancelText={t('confirmations.deleteCancel')}
        variant="destructive"
      />

      <ConfirmationDialog
        isOpen={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        onConfirm={confirmDiscardEdit}
        title={t('memoryDetail.discardDialog.title')}
        description={t('memoryDetail.confirmations.cancelWithChanges')}
        confirmText={t('memoryDetail.discardDialog.confirm')}
        cancelText={t('memoryDetail.discardDialog.cancel')}
        variant="default"
      />
    </>
  );
};

export default function MemoryDetailModal({
  memoryId,
  isOpen,
  onClose,
  onMemoryUpdate,
  onMemoryDelete,
}: MemoryDetailModalProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const { t: commonT } = useTranslation('common');

  useEffect(() => {
    if (isOpen) {
      setMode('view');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex min-h-full items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/75 supports-[backdrop-filter]:bg-black/55 backdrop-blur-md"
        onClick={onClose}
        aria-hidden
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className={cn(
          'relative z-10 flex w-full max-w-2xl min-h-0 max-h-[90vh] flex-col overflow-hidden rounded-lg border border-workspace-border',
          'bg-workspace-background-primary shadow-workspace-strong'
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className={cn(
            'absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full',
            'border border-workspace-border bg-workspace-background-secondary hover:bg-workspace-background-tertiary',
            'text-workspace-text-primary transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-workspace-primary-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-workspace-background-primary'
          )}
          aria-label={commonT('buttons.close')}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto p-6 pt-14">
          <MemoryDetailEdit
            memoryId={memoryId}
            mode={mode}
            onModeChange={setMode}
            onUpdate={onMemoryUpdate}
            onDelete={() => onMemoryDelete(memoryId)}
            onClose={onClose}
          />
        </div>
      </motion.div>
    </div>
  );
}
